"use client";

import { useEffect } from "react";

import EmailForm from "@/app/(portal)/(unauthenticated)/(forms)/email";
import OtpVerificationForm from "@/app/(portal)/(unauthenticated)/(forms)/otp-verification";
import PhoneForm from "@/app/(portal)/(unauthenticated)/(forms)/phone";
import VerificationHelpForm from "@/app/(portal)/(unauthenticated)/(forms)/verification-help";
import XeroLoginLogoSVG from "@/components/pop/xero/xero-login-logo.svg";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLogin } from "@/lib/login/context";
import Link from "next/link";
import { redirect, useSearchParams } from "next/navigation";

const ERROR_MESSAGES: Record<string, string> = {
  user_not_found: "User not found.",
};

export default function Page() {
  const searchParams = useSearchParams();
  const { toast } = useToast();

  const errorCode = searchParams.get("error");

  const {
    verification,
    showVerificationHelp,
    setVerification,
    setShowVerificationHelp,
  } = useLogin();

  useEffect(() => {
    if (errorCode) {
      // Adding a timeout or the Toast doesn't show up
      const timeOut = setTimeout(() => {
        toast({
          title: ERROR_MESSAGES[errorCode] || "An error occurred",
          description: "Did you use the same method you signed up with?",
          variant: "destructive",
        });
      }, 500);
      return () => clearTimeout(timeOut);
    }
  }, [errorCode, toast]);

  if (showVerificationHelp) {
    return (
      <SharedLayout>
        <VerificationHelpForm
          phoneNumber={
            verification.method === "sms" ? verification.value! : undefined
          }
          email={
            verification.method === "email" ? verification.value! : undefined
          }
          onClickReset={() => {
            setVerification({ method: "sms", value: null });
            setShowVerificationHelp(false);
          }}
          onRequested={() => {
            setShowVerificationHelp(false);
          }}
        />
      </SharedLayout>
    );
  }

  if (verification.value) {
    return (
      <SharedLayout>
        <OtpVerificationForm
          method={verification.method}
          recipient={verification.value}
          onConfirmed={() => {
            redirect("/dashboard");
          }}
          onClickHelp={() => setShowVerificationHelp(true)}
          onError={() => setShowVerificationHelp(true)}
        />
      </SharedLayout>
    );
  }

  return (
    <SharedLayout>
      <div>
        <h1 className="text-color-text-branded text-left text-sm leading-[150%] font-semibold uppercase">
          Let&rsquo;s Get Back to Business
        </h1>
        <h2 className="text-xl leading-[125%] font-light">
          Sign in with your phone number or email.
        </h2>
      </div>

      <Tabs defaultValue="phone">
        <TabsList className="flex w-full">
          <TabsTrigger className="flex-1 text-sm" value="phone">
            Phone
          </TabsTrigger>
          <TabsTrigger className="flex-1 text-sm" value="email">
            Email
          </TabsTrigger>
        </TabsList>
        <TabsContent value="phone">
          <PhoneForm />
        </TabsContent>
        <TabsContent value="email">
          <EmailForm />
        </TabsContent>
      </Tabs>

      <div className="prime-only w-full flex-col gap-6">
        <div className="flex w-full items-center justify-center gap-[6px]">
          <div className="bg-txt-primary h-px w-full" />
          <h3 className="text-border-dark text-[10px]">OR</h3>
          <div className="bg-txt-primary h-px w-full" />
        </div>
        <h3 className="text-xl leading-[125%] font-light">
          Sign in with your accounting software.
        </h3>
        <Button
          className="h-12 w-full gap-3 rounded-xl bg-[#1AB4D7] px-5 py-3 text-sm font-medium hover:bg-[#1590AC]"
          asChild
        >
          <Link href={"/xero/auth"}>
            <XeroLoginLogoSVG className="h-8 w-8" />
            Sign in with Xero
          </Link>
        </Button>
      </div>
    </SharedLayout>
  );
}

function SharedLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen w-screen flex-col">
      <div className="relative box-border flex w-full flex-row items-center justify-center border-b-[1px] border-solid p-6 text-left text-[13px] md:bg-[unset]">
        <div className="flex w-[1392px] max-w-[1440px] flex-1 flex-row items-center justify-between">
          <div className="navigation-logo h-8 md:h-[42px]">&nbsp;</div>
        </div>
      </div>
      <div className="flex w-full flex-1 items-start justify-center">
        <div className="flex w-full max-w-[480px] flex-col gap-6 p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
